import { FC, useCallback, useState } from 'react';
import classNames from 'classnames';

import { availableFigures, Figure, PlacedFigure } from './types';
import { FigureRenderer } from './FigureRenderer';
import { SandTherapyBoard } from './SandTherapyBoard';

function getFigureHeightClass(size: Figure['size']) {
  switch (size) {
    case 'small':
      return 'h-10';
    case 'medium':
      return 'h-14';
    case 'large':
      return 'h-16';
  }
}

interface SandTherapyClientProps {
  onSelectFigures?: (figures: Figure[]) => void;
  onStartPlacement?: () => void;
  onPlaceFigure?: (figure: PlacedFigure) => void;
  onMoveFigure?: (id: string, x: number, y: number) => void;
  onRotateFigure?: (id: string) => void;
  onResizeFigure?: (id: string, scale: number) => void;
  onRemoveFigure?: (id: string) => void;
  onSandColorChange?: (color: string) => void;
  onClientReady?: () => void;
  placedFigures: PlacedFigure[];
  sandColor: string;
  clientReady: boolean;
}

export const SandTherapyClient: FC<SandTherapyClientProps> = ({
  onSelectFigures,
  onStartPlacement,
  onPlaceFigure,
  onMoveFigure,
  onRotateFigure,
  onResizeFigure,
  onRemoveFigure,
  onSandColorChange,
  onClientReady,
  placedFigures,
  sandColor,
  clientReady,
}) => {
  const [phase, setPhase] = useState<'select' | 'place'>('select');
  const [selectedFigures, setSelectedFigures] = useState<Figure[]>([]);

  const toggleFigure = useCallback((fig: Omit<Figure, 'id'>) => {
    setSelectedFigures((prev) => {
      const existing = prev.findIndex((f) => f.type === fig.type && f.color === fig.color && f.size === fig.size);
      if (existing >= 0) {
        return prev.filter((_, i) => i !== existing);
      }
      return [...prev, { ...fig, id: `${fig.type}-${fig.color}-${Date.now()}-${Math.random()}` }];
    });
  }, []);

  const isFigureSelected = useCallback(
    (fig: Omit<Figure, 'id'>) =>
      selectedFigures.some((f) => f.type === fig.type && f.color === fig.color && f.size === fig.size),
    [selectedFigures],
  );

  const handleStartPlacement = useCallback(() => {
    onSelectFigures?.(selectedFigures);
    onStartPlacement?.();
    setPhase('place');
  }, [selectedFigures, onSelectFigures, onStartPlacement]);

  if (phase === 'place' || clientReady) {
    return (
      <div className="h-full w-full flex flex-col bg-[#525458] text-white font-comfortaa p-4">
        <h2 className="text-xl font-bold">Песочная терапия</h2>
        <p className="text-white/70 text-xs font-comfortaa mb-3">
          Расставьте фигуры на поле. Перетаскивайте их для изменения положения.
        </p>
        <div className="flex-1 min-h-0">
          <SandTherapyBoard
            selectedFigures={selectedFigures}
            placedFigures={placedFigures}
            onPlaceFigure={(fig) => onPlaceFigure?.(fig)}
            onMoveFigure={(id, x, y) => onMoveFigure?.(id, x, y)}
            onRotateFigure={(id) => onRotateFigure?.(id)}
            onResizeFigure={(id, scale) => onResizeFigure?.(id, scale)}
            onRemoveFigure={(id) => onRemoveFigure?.(id)}
            onSandColorChange={onSandColorChange}
            onClientReady={onClientReady}
            sandColor={sandColor}
            clientReady={clientReady}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-[1200px] min-w-[1200px] flex flex-col bg-[#525458] text-white font-comfortaa relative">
      <div className="flex-shrink-0 p-4 pb-2">
        <h2 className="text-xl font-bold mb-1">Песочная терапия</h2>
        <p className="text-sm text-white/70 font-manrope">Выберите фигуры, которые хотите использовать</p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4">
        <div className="space-y-4 pb-[90px]">
          {[
            { label: 'Семья', types: ['family-elder', 'family-adult', 'family-teen', 'family-child', 'family-baby'] },
            { label: 'Эмоции', types: ['emotional-calm', 'emotional-upset', 'emotional-happy'] },
            { label: 'Окружение', types: ['house', 'tree'] },
            { label: 'Животные', types: ['animal-dog', 'animal-cat', 'animal-bird'] },
            { label: 'Специальные', types: ['cocoon', 'striped'] },
            { label: 'Люди', types: ['person', 'tall'] },
            { label: 'Шары', types: ['ball'] },
          ].map(({ label, types }) => {
            const figs = availableFigures.filter((f) => types.includes(f.type));
            return (
              <div key={label}>
                <p className="text-xs text-white/60 mb-2">{label}</p>
                <div className="grid grid-cols-4 gap-2">
                  {figs.map((fig, idx) => {
                    const selected = isFigureSelected(fig);
                    return (
                      <button
                        key={idx}
                        onClick={() => toggleFigure(fig)}
                        className={classNames(
                          'flex items-center justify-center p-2 rounded-lg border-2 transition-all',
                          selected
                            ? 'border-[#3498DB] bg-[#3498DB]/20'
                            : 'border-white/20 hover:border-white/40 bg-white/5',
                        )}
                        title={fig.name}
                      >
                        <div className={classNames('w-8', getFigureHeightClass(fig.size))}>
                          <FigureRenderer figure={fig} className="w-full h-full" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-2 bg-[#525458]">
        <div className="text-xs text-white/60 mb-2 text-center">Выбрано: {selectedFigures.length}</div>
        <button
          onClick={handleStartPlacement}
          disabled={selectedFigures.length === 0}
          className={classNames(
            'w-full h-[50px] rounded-[12px] font-comfortaa font-bold text-m transition-colors',
            selectedFigures.length > 0
              ? 'bg-[#C00000] text-white hover:bg-[#a00000] cursor-pointer'
              : 'bg-[#C00000]/40 text-white/60 cursor-not-allowed',
          )}
        >
          Начать работу с фигурами
        </button>
      </div>
    </div>
  );
};
